import { useEffect, useRef, useState, type FormEvent } from "react";
import { confirmDialog } from "./Confirm";
import { api } from "./lib/api";
import { loadGoogleMaps } from "./lib/googlemaps";

const MAP_ID = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string) || "DEMO_MAP_ID";
const STATE = "TX";

// Staff geocode-correction tool (Company Portal). Five modes:
//  - Address search: find one household, drag pin to rooftop, save.
//  - Browse precinct: pick district -> precinct, every household as status-colored pins.
//  - Cleanup (triage): a three-step sweep for one district — phantom parcel pins (bare street, zero
//    voters), then same-address-written-two-ways duplicates (0096; same ZIP + house number + unit,
//    fuzzy street name, repointed onto one survivor), then a ranked "to fix" queue (no-coord,
//    outside-precinct, placeholder, stacked clusters, low quality), worst-first. Clusters open a
//    batch flow that walks address 1..N with auto-advance. Everything soft-deletes and audits, and
//    human-placed coords are stored sticky.
//  - Overview / auto-resolve and Realign districts round it out.
//
// A FAILED SCAN IS NOT A CLEAN DISTRICT: each cleanup step tracks whether it actually ran
// (`scanFailed`), because "this district's geocodes look clean" is what someone reads before cutting
// walkbooks from it.
type HH = {
  id: string; street: string | null; unit: string | null; city: string | null; zip: string | null;
  precinct: string | null; lat: number | null; lon: number | null;
  geocode_quality: string | null; geocode_source: string | null; is_placeholder_coord?: boolean;
};
type Suspect = HH & { reason: string; cluster_key: string | null; cluster_size: number };
type ARZip = { id: string; street: string; city: string; file_zip: string; authority_zip: string };
type ARMove = { id: string; street: string; stored: (number | null)[]; authority: (number | null)[]; dist_m: number | null };
type ARSummary = {
  scanned: number; certify: number; zip_fix: number; regeocode: number; manual: number;
  applied: number; zip_fixed: number; manual_reasons: Record<string, number>;
  zip_mismatches: ARZip[]; regeocodes: ARMove[];
};
type RealignMove = { id: string; street: string; city: string; lat: number | null; lon: number | null; old: string | null; new: string | null };
type RealignPreview = { changed_hd: number; changed_cd: number; changed_sd: number; hd_sample: RealignMove[]; hd_dests: Record<string, number> };
type RealignResult = { scanned: number; changed_hd: number; changed_cd: number; changed_sd: number };
type DupSide = {
  id: string; street: string | null; unit: string | null; city: string | null; zip: string | null;
  lat: number | null; lon: number | null; voters: number; source: string | null;
};
type DupePair = { a: DupSide; b: DupSide; sim: number; colocated: boolean; suggested_survivor: string | null; tier: string };
type Phantoms = { count: number; sample: HH[]; blank_stubs: number; street_only: number };
type ExactDupes = { groups: number; households: number; extra_records: number };
type ExactGroupMember = { id: string; street: string | null; unit: string | null; city: string | null; zip: string | null; lat: number | null; lon: number | null; voters: number; survivor: boolean };
type ExactGroup = { key: string; coords_uniform: boolean; count: number; members: ExactGroupMember[] };
type LogEntry = { id: string; label: string; kind: "merge" | "move" | "review"; mergeIds?: string[]; householdIds?: string[]; items?: Suspect[]; undone?: boolean };
type HistEvent = { kind: string; household_id: string | null; address: string; at: string | null; who: string | null; undone: boolean; ref: string; detail: string | null };
const sideAddr = (h: DupSide) => [h.street, h.unit].filter(Boolean).join(" ") + (h.city ? `, ${h.city}` : "") + (h.zip ? ` ${h.zip}` : "");
const pairKey = (p: DupePair) => p.a.id + "|" + p.b.id;
const addr = (h: HH) => [h.street, h.unit].filter(Boolean).join(" ") + (h.city ? `, ${h.city}` : "") + (h.zip ? ` ${h.zip}` : "");
const memAddr = (m: { street: string | null; unit: string | null; city: string | null; zip: string | null }) =>
  [m.street, m.unit].filter(Boolean).join(" ") + (m.city ? `, ${m.city}` : "") + (m.zip ? ` ${m.zip}` : "");
const UNIT_RE = /(^|\s|#)(APT|APARTMENT|UNIT|STE|SUITE|BLDG|BUILDING|FL|FLR|FLOOR|RM|ROOM|LOT|TRLR|SPC|SPACE)\b/i;
const isApt = (h: HH) => !!(h.unit && h.unit.trim()) || (!!h.street && (UNIT_RE.test(h.street) || /#/.test(h.street)));
const recommendSurvivor = (members: HH[]): string => {
  const score = (m: HH) => (m.geocode_source === "human" ? 8 : 0) + (m.zip ? 4 : 0) + (m.city ? 2 : 0) + Math.min(1, (m.street?.length || 0) / 40);
  return [...members].sort((a, b) => score(b) - score(a))[0]?.id || members[0].id;
};
const BORDER = "1px solid rgba(128,128,128,.35)";

type Status = "verified" | "bad" | "ok";
const statusOf = (h: HH): Status =>
  h.geocode_source === "human" ? "verified"
  : (h.is_placeholder_coord || h.lat == null || h.lon == null) ? "bad" : "ok";
const STATUS_COLOR: Record<Status, string> = { verified: "#3fc079", bad: "#e0553f", ok: "#7d8794" };

const REASON: Record<string, { label: string; color: string }> = {
  no_coord:         { label: "No coordinate",   color: "#e0553f" },
  outside_precinct: { label: "Outside precinct", color: "#e08b3f" },
  placeholder:      { label: "Placeholder",      color: "#d14fb0" },
  cluster:          { label: "Stacked cluster",  color: "#c9433f" },
  low_quality:      { label: "Low quality",      color: "#b0863f" },
};
const reasonColor = (r: string) => REASON[r]?.color || "#e0553f";

const SCOPE_TYPES: { v: string; label: string }[] = [
  { v: "county", label: "County" },
  { v: "state_house", label: "State House" },
  { v: "state_senate", label: "State Senate" },
  { v: "congressional", label: "Congressional" },
];
type Region = { code: string; name: string | null };
type PrecinctRow = { county: string | null; precinct: string; n_total: number; n_verified: number; n_placeholder: number; n_nocoord: number };

export function Geocode() {
  const [mode, setMode] = useState<"overview" | "search" | "browse" | "cleanup" | "realign">("overview");
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [placeTarget, setPlaceTarget] = useState<{ ids: string[]; label: string; centroid: { lat: number; lon: number } | null } | null>(null);
  const [separateKey, setSeparateKey] = useState<string | null>(null);
  const [mergePick, setMergePick] = useState<{ key: string; members: Suspect[]; survivorId: string } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [sel, setSel] = useState<HH | null>(null);
  const [dragged, setDragged] = useState<{ lat: number; lon: number } | null>(null);
  const [saved, setSaved] = useState(false);
  const [ready, setReady] = useState(false);

  // search
  const [q, setQ] = useState("");
  const [results, setResults] = useState<HH[]>([]);

  // shared scope pickers (browse + triage)
  const [scopeType, setScopeType] = useState("county");
  // WHETHER AUTO-RESOLVE CAN DO ANYTHING IN THIS DISTRICT. It only writes CORROBORATED answers --
  // two authorities agreeing within 150 m -- and there are two authorities: Census (statewide) and
  // the Bexar locator (Bexar only). In the other 253 counties everything correctly falls to manual,
  // and the screen used to promise the opposite right up until it returned three zeros.
  const [auth, setAuth] = useState<{ authorities: string[]; can_corroborate: boolean; known: boolean } | null>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [scopeValue, setScopeValue] = useState("");

  // overview (district cleanliness board)
  const [overview, setOverview] = useState<{ precincts: PrecinctRow[]; phantoms: number | null; dupeGroups: number | null; suspects: number | null; byReason: Record<string, number> } | null>(null);
  const [ovLoading, setOvLoading] = useState(false);   // loading-ok: runs only on an explicit button press; failures render as — with a note, not as zeroes
  // Auto-resolve (SOP): authorities (Bexar + Census) fix the corroborated majority; humans get the residual.
  const [arPreview, setArPreview] = useState<ARSummary | null>(null);
  const [arResult, setArResult] = useState<ARSummary | null>(null);
  const [arBusy, setArBusy] = useState(false);
  const [arFixZip, setArFixZip] = useState(true);
  // Realign (SOP step 2): recompute cd/sd/hd from verified coordinates; confirm on the map.
  const [rlPreview, setRlPreview] = useState<RealignPreview | null>(null);
  const [rlResult, setRlResult] = useState<RealignResult | null>(null);
  const [rlBusy, setRlBusy] = useState(false);
  const [rlDistPick, setRlDistPick] = useState<string | null>(null);
  const rlPolyRef = useRef<any>(null);
  const rlPinRef = useRef<any[]>([]);

  // browse
  const [precincts, setPrecincts] = useState<PrecinctRow[]>([]);
  const [curPrecinct, setCurPrecinct] = useState<PrecinctRow | null>(null);
  const [hh, setHh] = useState<HH[]>([]);
  const [loadingP, setLoadingP] = useState(false);   // loading-ok: explicit-search flag (no fetch on mount, so no first-paint flash)
  const [loadingH, setLoadingH] = useState(false);   // loading-ok: explicit-search flag

  // triage
  const [suspects, setSuspects] = useState<Suspect[]>([]);
  const [loadingS, setLoadingS] = useState(false);   // loading-ok: explicit-search flag
  const [cluster, setCluster] = useState<{ ids: string[]; idx: number; key: string } | null>(null);
  const [geoQ, setGeoQ] = useState("");
  const [lastSaved, setLastSaved] = useState<Suspect | HH | null>(null);

  // duplicates (de-dupe)
  const [phantoms, setPhantoms] = useState<Phantoms | null>(null);
  const [exactDupes, setExactDupes] = useState<ExactDupes | null>(null);
  const [lastBulk, setLastBulk] = useState<string[] | null>(null);
  const [dupePairs, setDupePairs] = useState<DupePair[]>([]);
  const [dupeTotal, setDupeTotal] = useState(0);
  const [loadingD, setLoadingD] = useState(false);   // loading-ok: explicit-scan flag; per-scan failures are tracked in scanFailed so no step claims to be clean
  /** Which of the four cleanup scans failed. A failed scan must never render as "nothing to fix". */
  const [scanFailed, setScanFailed] = useState({ phantoms: false, dupes: false, suspects: false });
  const [survivorPick, setSurvivorPick] = useState<Record<string, string>>({});
  const [lastMerge, setLastMerge] = useState<{ merge_id: string; moved: any } | null>(null);
  const dupPinRef = useRef<any[]>([]);
  const [exactGroups, setExactGroups] = useState<ExactGroup[]>([]);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [actionLog, setActionLog] = useState<LogEntry[]>([]);
  const [histOpen, setHistOpen] = useState(false);
  const [history, setHistory] = useState<HistEvent[]>([]);
  const [histLoading, setHistLoading] = useState(false);   // loading-ok: opened on demand from the History tab
  const previewRef = useRef<any[]>([]);

  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const layerRef = useRef<Map<string, any>>(new Map());
  const googleRef = useRef<any>(null);
  const susRef = useRef<Suspect[]>([]);
  susRef.current = suspects;

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    loadGoogleMaps().then((google: any) => {
      googleRef.current = google;
      mapRef.current = new google.maps.Map(elRef.current as HTMLElement, {
        center: { lat: 29.5, lng: -98.5 }, zoom: 11, mapId: MAP_ID, mapTypeId: "hybrid",
        mapTypeControl: true, streetViewControl: false, fullscreenControl: false, gestureHandling: "greedy",
      });
      setReady(true);
    }).catch((e: any) => setMsg(String(e?.message || e)));
  }, []);

  async function search(e?: FormEvent) {
    e?.preventDefault();
    const term = q.trim();
    if (term.length < 3) { setResults([]); setMsg("Type at least 3 characters."); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ households: HH[] }>(`/geocode/search?q=${encodeURIComponent(term)}`);
      setResults(r.households || []);
      if (!(r.households || []).length) setMsg("No matching households.");
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  useEffect(() => {
    setScopeValue(""); setPrecincts([]); setCurPrecinct(null); setHh([]); setSuspects([]); setOverview(null);
    setArPreview(null); setArResult(null);
    setRlPreview(null); setRlResult(null); setRlDistPick(null);
    setPhantoms(null); setDupePairs([]); setDupeTotal(0); setLastMerge(null); setSurvivorPick({});
    if (!scopeType) { setRegions([]); return; }
    let cancel = false;
    api<{ districts: Region[] }>(`/districts?state=${STATE}&scope_type=${encodeURIComponent(scopeType)}`)
      .then((r) => { if (!cancel) setRegions(r.districts || []); })
      .catch(() => { if (!cancel) setRegions([]); });
    return () => { cancel = true; };
  }, [scopeType]);

  // county= only when the district IS a county. A house district spans several and coverage cannot
  // be stated as one answer, so the endpoint says `known: false` and the screen says so too rather
  // than picking one county's answer and presenting it as the district's.
  useEffect(() => {
    setAuth(null);
    if (!scopeValue) return;
    const q = scopeType === "county" ? `?county=${encodeURIComponent(scopeValue)}` : "";
    api<{ authorities: string[]; can_corroborate: boolean; known: boolean }>(`/geocode/authorities${q}`)
      .then(setAuth).catch(() => setAuth(null));
  }, [scopeType, scopeValue]);

  async function loadPrecincts() {
    if (!scopeValue) return;
    setLoadingP(true); setMsg(null); setCurPrecinct(null); setHh([]);
    try {
      const r = await api<{ precincts: PrecinctRow[] }>(
        `/geocode/precincts?state=${STATE}&scope_type=${encodeURIComponent(scopeType)}&scope_value=${encodeURIComponent(scopeValue)}`);
      setPrecincts(r.precincts || []);
      if (!(r.precincts || []).length) setMsg("No precincts found for that selection.");
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setLoadingP(false); }
  }

  async function openPrecinct(p: PrecinctRow) {
    setCurPrecinct(p); setLoadingH(true); setMsg(null); setSel(null); setDragged(null); setSaved(false);
    try {
      const r = await api<{ households: HH[] }>(
        `/geocode/precinct-households?state=${STATE}&scope_type=${encodeURIComponent(scopeType)}` +
        `&scope_value=${encodeURIComponent(scopeValue)}&county=${encodeURIComponent(p.county || "")}` +
        `&precinct=${encodeURIComponent(p.precinct)}&limit=8000`);
      setHh(r.households || []);
    } catch (e: any) { setMsg(e?.message || String(e)); setHh([]); }
    finally { setLoadingH(false); }
  }

  async function loadSuspects() {
    if (!scopeValue) return;
    setLoadingS(true); setMsg(null); setSel(null); setCluster(null); setDragged(null); setSaved(false);
    try {
      const r = await api<{ suspects: Suspect[] }>(
        `/geocode/suspects?state=${STATE}&scope_type=${encodeURIComponent(scopeType)}&scope_value=${encodeURIComponent(scopeValue)}`);
      setSuspects(r.suspects || []);
      if (!(r.suspects || []).length) setMsg("Nothing flagged for fixing in this district — geocodes look clean.");
    } catch (e: any) { setMsg(e?.message || String(e)); setSuspects([]); }
    finally { setLoadingS(false); }
  }

  async function loadOverview() {
    if (!scopeValue) return;
    setOvLoading(true); setMsg(null); setOverview(null);
    const base = `state=${STATE}&scope_type=${encodeURIComponent(scopeType)}&scope_value=${encodeURIComponent(scopeValue)}`;
    // The precinct rollup IS the board — load it first and render even if the heavier district-wide
    // stat scans time out on a large district. The old single Promise.all failed the whole page if
    // any one query hit the statement timeout (the HD-121 503).
    let precincts: PrecinctRow[] = [];
    try {
      precincts = (await api<{ precincts: PrecinctRow[] }>(`/geocode/precincts?${base}`)).precincts || [];
    } catch {
      setMsg("This district is large — the precinct rollup timed out. Open Browse or Triage one precinct at a time.");
      setOvLoading(false); return;
    }
    // best-effort district totals: a timeout on any one shows as "—", it never breaks the board.
    const [phR, exR, suR] = await Promise.allSettled([
      api<Phantoms>(`/geocode/phantoms?${base}`),
      api<ExactDupes>(`/geocode/exact-dupes?${base}`),
      api<{ suspects: Suspect[] }>(`/geocode/suspects?${base}`),
    ]);
    const phantoms = phR.status === "fulfilled" ? (phR.value.count || 0) : null;
    const dupeGroups = exR.status === "fulfilled" ? (exR.value.groups || 0) : null;
    const byReason: Record<string, number> = {};
    let suspects: number | null = null;
    if (suR.status === "fulfilled") {
      (suR.value.suspects || []).forEach((x) => { byReason[x.reason] = (byReason[x.reason] || 0) + 1; });
      suspects = (suR.value.suspects || []).length;
    }
    setOverview({ precincts, phantoms, dupeGroups, suspects, byReason });
    if (!precincts.length) setMsg("No precincts found for that selection.");
    setOvLoading(false);
  }

  async function autoResolve(apply: boolean) {
    if (!scopeValue) return;
    setArBusy(true); setMsg(null);
    try {
      const r = await api<{ ok: boolean; apply: boolean; summary: ARSummary }>(`/geocode/auto-resolve`, {
        method: "POST",
        body: JSON.stringify({ state: STATE, scope_type: scopeType, scope_value: scopeValue, apply, fix_zip: arFixZip, limit: 20000 }),
      });
      if (apply) { setArResult(r.summary); setArPreview(null); }
      else { setArPreview(r.summary); setArResult(null); }
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setArBusy(false); }
  }
  const arDist = (m: number | null) => m == null ? "" : (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);
  async function realign(apply: boolean) {
    if (!scopeValue) return;
    setRlBusy(true); setMsg(null);
    try {
      const r = await api<any>(`/geocode/realign-districts`, {
        method: "POST",
        body: JSON.stringify({ state: STATE, scope_type: scopeType, scope_value: scopeValue, apply, limit: 3000 }),
      });
      if (apply) { setRlResult(r as RealignResult); setRlPreview(null); setRlDistPick(null); }
      else { setRlPreview(r as RealignPreview); setRlResult(null); setRlDistPick(null); }
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setRlBusy(false); }
  }
  function geoPaths(geom: any): { lat: number; lng: number }[][] {
    if (!geom) return [];
    const rings: any[] = geom.type === "MultiPolygon" ? geom.coordinates.flat() : geom.type === "Polygon" ? geom.coordinates : [];
    return rings.map((ring: any) => ring.map((c: any) => ({ lat: c[1], lng: c[0] })));
  }

  function openPrecinctFrom(p: PrecinctRow) { setMode("browse"); openPrecinct(p); }

  async function scanCleanup() {
    if (!scopeValue) return;
    setLoadingD(true); setMsg(null); setSel(null); setDragged(null); setPlaceTarget(null); setCluster(null); setSeparateKey(null);
    setPhantoms(null); setDupePairs([]); setDupeTotal(0); setLastMerge(null); setSurvivorPick({});
    setExactDupes(null); setLastBulk(null); setSuspects([]); setExactGroups([]);
    clearDupPins(); clearPreview();
    const base = `state=${STATE}&scope_type=${encodeURIComponent(scopeType)}&scope_value=${encodeURIComponent(scopeValue)}`;
    // One scan feeds all three cleanup steps. allSettled so a timeout on one heavy scan (large
    // district) still populates the rest — the steps that loaded stay usable.
    // 4 parallel scans (the exact-dupe COUNT is derived from the groups call below, so we don't
    // run the redundant /geocode/exact-dupes summary — one fewer heavy district scan = fewer timeouts).
    const [phR, dpR, suR, egR] = await Promise.allSettled([
      api<Phantoms>(`/geocode/phantoms?${base}`),
      api<{ pairs: DupePair[]; total: number }>(`/geocode/dupe-candidates?${base}&limit=400`),
      api<{ suspects: Suspect[] }>(`/geocode/suspects?${base}`),
      api<{ groups: ExactGroup[] }>(`/geocode/exact-dupe-groups?${base}`),
    ]);
    // A SCAN THAT FAILED IS NOT A CLEAN DISTRICT.
    //
    // Each of these used to leave its state empty on rejection, and every step then printed its
    // empty message as fact — "None in this scope", "No duplicates in this scope", and worst of all
    // "Nothing left to fix — this district's geocodes look clean." That last sentence is the one a
    // staffer reads before signing a district off to cut walkbooks from. A banner at the top of the
    // page said some scan had timed out, but not which, and it sat nowhere near the panel making the
    // claim. Each step now knows whether it actually ran.
    setScanFailed({
      phantoms: phR.status === "rejected",
      dupes: dpR.status === "rejected" || egR.status === "rejected",
      suspects: suR.status === "rejected",
    });
    if (phR.status === "fulfilled") setPhantoms(phR.value);
    if (dpR.status === "fulfilled") { setDupePairs(dpR.value.pairs || []); setDupeTotal(dpR.value.total || 0); }
    if (suR.status === "fulfilled") setSuspects(suR.value.suspects || []);
    if (egR.status === "fulfilled") {
      const gs = egR.value.groups || [];
      setExactGroups(gs);
      setExactDupes({ groups: gs.length, households: gs.reduce((a, g) => a + g.count, 0),
                      extra_records: gs.reduce((a, g) => a + Math.max(0, g.count - 1), 0) });
    }
    if ([phR, dpR, suR, egR].some((r) => r.status === "rejected"))
      setMsg("This district is large — one or more scans timed out; the steps that loaded are usable. Re-scan to retry.");
    setStep(1); setLoadingD(false);
  }

  async function savePlacement() {
    if (!placeTarget || !dragged) return;
    const ids = placeTarget.ids;
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ updated: number }>(`/geocode/correct-batch`, {
        method: "POST",
        body: JSON.stringify({ household_ids: ids, lat: dragged.lat, lon: dragged.lon }),
      });
      const movedItems = susRef.current.filter((sp) => ids.includes(sp.id));
      setSuspects((rs) => rs.filter((sp) => !ids.includes(sp.id)));
      ids.forEach((id) => { const bm = layerRef.current.get(id); if (bm) { bm.map = null; layerRef.current.delete(id); } });
      pushLog({ kind: "move", label: `Moved ${ids.length} address${ids.length === 1 ? "" : "es"}`, householdIds: ids, items: movedItems });
      setMsg(`Placed ${r.updated} address${r.updated === 1 ? "" : "es"} on the rooftop — human-verified.`);
      setPlaceTarget(null); setDragged(null); setSel(null); setSeparateKey(null);
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  async function removePhantoms() {
    if (!phantoms || !phantoms.count) return;
    if (!(await confirmDialog(`Remove ${phantoms.count} no-voter / no-address pins in this scope?\n\n${phantoms.blank_stubs} blank stubs + ${phantoms.street_only} street-only placeholders. None has a house number, ZIP, or registered voter (parcel-service leftovers). Soft-deleted (reversible), not erased.`, { danger: true, confirmLabel: "Remove pins" }))) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ removed: number }>(`/geocode/remove-phantoms`, {
        method: "POST",
        body: JSON.stringify({ state: STATE, scope_type: scopeType, scope_value: scopeValue }),
      });
      setPhantoms({ count: 0, sample: [], blank_stubs: 0, street_only: 0 });
      setMsg(`Removed ${r.removed} phantom pins — soft-deleted and reversible in the database.`);
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  function chosenSurvivor(p: DupePair) {
    return survivorPick[pairKey(p)] || p.suggested_survivor || (p.a.voters >= p.b.voters ? p.a.id : p.b.id);
  }

  async function mergePair(p: DupePair) {
    const survivor = chosenSurvivor(p);
    const loser = survivor === p.a.id ? p.b.id : p.a.id;
    setBusy(true); setMsg(null);
    try {
      const r = await api<any>(`/geocode/merge`, {
        method: "POST",
        body: JSON.stringify({ survivor_id: survivor, loser_id: loser }),
      });
      setDupePairs((ps) => ps.filter((x) => pairKey(x) !== pairKey(p)));
      setLastMerge({ merge_id: r.merge_id, moved: r.moved || {} });
      pushLog({ kind: "merge", label: `Merged ${sideAddr(survivor === p.a.id ? p.a : p.b)}`, mergeIds: [r.merge_id] });
      clearDupPins();
      const mv = r.moved || {};
      setMsg(`Merged — moved ${mv.voter || 0} voters${mv.voter_overlay ? ` + ${mv.voter_overlay} overlay` : ""} onto the survivor. Undo available.`);
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  async function undoMerge() {
    if (!lastMerge) return;
    setBusy(true); setMsg(null);
    try {
      await api(`/geocode/unmerge`, { method: "POST", body: JSON.stringify({ merge_id: lastMerge.merge_id }) });
      setMsg("Merge undone — records repointed back to the original household. Re-scan to see it again.");
      setLastMerge(null);
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  async function mergeExactAll() {
    if (!exactDupes || !exactDupes.groups) return;
    if (!(await confirmDialog(`Merge all ${exactDupes.groups} exact-duplicate address groups (${exactDupes.extra_records} extra records fold into a survivor)?\n\nEvery member has the identical street + ZIP + unit, so this only unions distinct voters onto one record — voter counts never inflate. Reversible.`, { danger: true, confirmLabel: "Merge all" }))) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ groups: number; merged: number; merge_ids: string[] }>(`/geocode/merge-exact`, {
        method: "POST",
        body: JSON.stringify({ state: STATE, scope_type: scopeType, scope_value: scopeValue }),
      });
      setExactDupes({ groups: 0, households: 0, extra_records: 0 }); setExactGroups([]);
      setLastBulk(r.merge_ids || []); setLastMerge(null); clearDupPins();
      pushLog({ kind: "merge", label: `Merged ${r.merged} exact duplicates`, mergeIds: r.merge_ids || [] });
      setMsg(`Merged ${r.merged} duplicate records across ${r.groups} addresses. Undo available.`);
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  async function undoBulk() {
    if (!lastBulk || !lastBulk.length) return;
    setBusy(true); setMsg(null);
    try {
      for (const id of lastBulk) {
        await api(`/geocode/unmerge`, { method: "POST", body: JSON.stringify({ merge_id: id }) });
      }
      setMsg(`Undid ${lastBulk.length} merges — records repointed back. Re-scan to see them again.`);
      setLastBulk(null);
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  function clearDupPins() {
    dupPinRef.current.forEach((m) => (m.map = null));
    dupPinRef.current = [];
  }

  function clearPreview() {
    previewRef.current.forEach((m) => (m.map = null));
    previewRef.current = [];
  }

  // Read-only "show me where these are" preview: drops plain pins, fits bounds, no placement mode.
  function previewHouseholds(items: { id: string; lat: number | null; lon: number | null; street?: string | null; unit?: string | null }[]) {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map) return;
    clearPreview();
    const b = new google.maps.LatLngBounds();
    let any = false;
    items.forEach((h) => {
      if (h.lat == null || h.lon == null) return;
      const dot = document.createElement("div");
      dot.style.cssText = `width:15px;height:15px;border-radius:50%;background:#4f9dff;` +
        `border:1.5px solid rgba(0,0,0,.6);box-shadow:0 0 0 1px rgba(255,255,255,.45)`;
      const m = new google.maps.marker.AdvancedMarkerElement({ position: { lat: h.lat, lng: h.lon }, map, content: dot, title: [h.street, h.unit].filter(Boolean).join(" ") });
      previewRef.current.push(m);
      b.extend({ lat: h.lat, lng: h.lon }); any = true;
    });
    if (any) { map.fitBounds(b, 120); if ((map.getZoom() || 0) > 20) map.setZoom(20); }
    else setMsg("None of these have a coordinate to show on the map.");
  }

  function pushLog(e: Omit<LogEntry, "id">) {
    setActionLog((l) => [{ ...e, id: String(Date.now()) + Math.random() }, ...l]);
  }

  async function doMergeCluster() {
    if (!mergePick) return;
    const { members, survivorId } = mergePick;
    setBusy(true); setMsg(null);
    setCluster(null); setPlaceTarget(null); setSeparateKey(null); setDragged(null);
    try {
      const ids = members.map((m) => m.id);
      const r = await api<{ merged: number; survivor: string }>(`/geocode/merge-households`, { method: "POST", body: JSON.stringify({ household_ids: ids, survivor_id: survivorId }) });
      setSuspects((rs) => rs.filter((sp) => !ids.includes(sp.id)));
      ids.forEach((id) => { const bm = layerRef.current.get(id); if (bm) { bm.map = null; layerRef.current.delete(id); } });
      const surv = members.find((m) => m.id === survivorId) || null;
      setMergePick(null);
      setSel(surv);
      setMsg(`Merged ${r.merged} record${r.merged === 1 ? "" : "s"} in — adjust the pin if needed, then Save rooftop (or just move on).`);
      if (histOpen) loadHistory();
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  async function loadHistory() {
    if (!scopeValue) return;
    setHistLoading(true);
    try {
      const r = await api<{ events: HistEvent[] }>(`/geocode/history?state=${STATE}&scope_type=${encodeURIComponent(scopeType)}&scope_value=${encodeURIComponent(scopeValue)}&limit=300`);
      setHistory(r.events || []);
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setHistLoading(false); }
  }

  async function undoHistory(ev: HistEvent) {
    if (ev.undone) return;
    setBusy(true); setMsg(null);
    try {
      if (ev.kind === "moved") await api(`/geocode/undo`, { method: "POST", body: JSON.stringify({ household_id: ev.household_id }) });
      else if (ev.kind === "merged") await api(`/geocode/unmerge`, { method: "POST", body: JSON.stringify({ merge_id: ev.ref }) });
      else await api(`/geocode/review`, { method: "POST", body: JSON.stringify({ household_id: ev.household_id, status: "clear" }) });
      await loadHistory();
      setMsg("Undone — re-scan to see restored records.");
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  async function undoEntry(e: LogEntry) {
    if (e.undone) return;
    setBusy(true); setMsg(null);
    try {
      if (e.kind === "merge") {
        for (const id of e.mergeIds || []) await api(`/geocode/unmerge`, { method: "POST", body: JSON.stringify({ merge_id: id }) });
        setMsg("Merge undone — re-scan to see the restored records.");
      } else if (e.kind === "move") {
        await api(`/geocode/undo-batch`, { method: "POST", body: JSON.stringify({ household_ids: e.householdIds || [] }) });
        if (e.items?.length) setSuspects((prev) => [...e.items!, ...prev]);
        setMsg("Move undone — coordinates restored.");
      } else {
        for (const id of e.householdIds || []) await api(`/geocode/review`, { method: "POST", body: JSON.stringify({ household_id: id, status: "clear" }) });
        if (e.items?.length) setSuspects((prev) => [...e.items!, ...prev]);
        setMsg("Review undone — returned to the queue.");
      }
      setActionLog((l) => l.map((x) => (x.id === e.id ? { ...x, undone: true } : x)));
    } catch (err: any) { setMsg(err?.message || String(err)); }
    finally { setBusy(false); }
  }

  async function mergeGroup(g: ExactGroup) {
    const survivor = g.members.find((m) => m.survivor) || g.members[0];
    if (!survivor) return;
    const losers = g.members.filter((m) => m.id !== survivor.id);
    setBusy(true); setMsg(null);
    try {
      const mergeIds: string[] = [];
      for (const m of losers) {
        const r = await api<any>(`/geocode/merge`, { method: "POST", body: JSON.stringify({ survivor_id: survivor.id, loser_id: m.id }) });
        if (r?.merge_id) mergeIds.push(r.merge_id);
      }
      setExactGroups((gs) => gs.filter((x) => x.key !== g.key));
      setExactDupes((ex) => ex ? { groups: Math.max(0, ex.groups - 1), households: Math.max(0, ex.households - losers.length), extra_records: Math.max(0, ex.extra_records - losers.length) } : ex);
      pushLog({ kind: "merge", label: `Merged ${memAddr(survivor)} · ${g.count} records`, mergeIds });
      setMsg(`Merged ${losers.length} duplicate record${losers.length === 1 ? "" : "s"} into ${memAddr(survivor)}.`);
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  function focusPair(p: DupePair) {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map) return;
    clearDupPins();
    const b = new google.maps.LatLngBounds();
    let any = false;
    ([["A", p.a], ["B", p.b]] as [string, DupSide][]).forEach(([tag, h]) => {
      if (h.lat == null || h.lon == null) return;
      const survivor = chosenSurvivor(p);
      const isSurv = h.id === survivor;
      const dot = document.createElement("div");
      dot.style.cssText = `min-width:20px;height:20px;padding:0 5px;border-radius:11px;display:flex;align-items:center;` +
        `justify-content:center;font:700 11px system-ui;color:#fff;background:${isSurv ? "#3fc079" : "#e08b3f"};` +
        `border:1.5px solid rgba(0,0,0,.55);box-shadow:0 0 0 1px rgba(255,255,255,.4)`;
      dot.textContent = tag + (isSurv ? " keep" : "");
      const m = new google.maps.marker.AdvancedMarkerElement({ position: { lat: h.lat, lng: h.lon }, map, content: dot, title: sideAddr(h) });
      dupPinRef.current.push(m);
      b.extend({ lat: h.lat, lng: h.lon }); any = true;
    });
    if (any) { map.fitBounds(b, 140); if ((map.getZoom() || 0) > 20) map.setZoom(20); }
  }

  // pin layer: browse -> hh (status colors); triage -> suspects (reason colors)
  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map) return;
    layerRef.current.forEach((m) => (m.map = null));
    layerRef.current.clear();
    dupPinRef.current.forEach((m) => (m.map = null));
    dupPinRef.current = [];
    previewRef.current.forEach((m) => (m.map = null));
    previewRef.current = [];
    const list: HH[] = mode === "browse" ? hh : (mode === "cleanup" && step === 3) ? suspects : [];
    if (list.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    let plotted = 0;
    for (const h of list) {
      if (h.lat == null || h.lon == null) continue;
      const color = (mode === "cleanup" && step === 3) ? reasonColor((h as Suspect).reason) : STATUS_COLOR[statusOf(h)];
      const dot = document.createElement("div");
      dot.style.cssText = `width:13px;height:13px;border-radius:50%;background:${color};` +
        `border:1.5px solid rgba(0,0,0,.55);cursor:pointer;box-shadow:0 0 0 1px rgba(255,255,255,.35)`;
      const m = new google.maps.marker.AdvancedMarkerElement({ position: { lat: h.lat, lng: h.lon }, map, content: dot, title: addr(h) });
      dot.addEventListener("click", () => setSel(h));
      layerRef.current.set(h.id, m);
      bounds.extend({ lat: h.lat, lng: h.lon });
      plotted++;
    }
    if (plotted > 0) map.fitBounds(bounds, 40);
  }, [hh, suspects, mode, step, ready]);

  // Realign confirmation overlay: draw the picked destination district's boundary + its moved pins.
  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map) return;
    if (rlPolyRef.current) { rlPolyRef.current.setMap(null); rlPolyRef.current = null; }
    rlPinRef.current.forEach((m) => (m.map = null)); rlPinRef.current = [];
    if (mode !== "realign" || !rlDistPick || !rlPreview) return;
    const dest = rlDistPick; let cancelled = false;
    (async () => {
      try {
        const fc = await api<{ features: any[] }>(`/boundaries?state=${STATE}&kind=state_house&name=${encodeURIComponent(dest)}`);
        if (cancelled) return;
        const bounds = new google.maps.LatLngBounds();
        const feat = (fc.features || [])[0];
        if (feat) {
          const paths = geoPaths(feat.geometry);
          rlPolyRef.current = new google.maps.Polygon({ paths, map, strokeColor: "#e08b3f", strokeOpacity: 0.9, strokeWeight: 2, fillColor: "#e08b3f", fillOpacity: 0.08, clickable: false });
          paths.forEach((ring) => ring.forEach((pt) => bounds.extend(pt)));
        }
        for (const z of rlPreview.hd_sample) {
          if (z.new !== dest || z.lat == null || z.lon == null) continue;
          let inside = true;
          try { if (feat && google.maps.geometry?.poly) inside = google.maps.geometry.poly.containsLocation(new google.maps.LatLng(z.lat, z.lon), rlPolyRef.current); } catch { inside = true; }
          const dot = document.createElement("div");
          dot.style.cssText = `width:12px;height:12px;border-radius:50%;background:${inside ? "#3fc079" : "#e0553f"};border:1.5px solid rgba(0,0,0,.55);box-shadow:0 0 0 1px rgba(255,255,255,.4)`;
          const m = new google.maps.marker.AdvancedMarkerElement({ position: { lat: z.lat, lng: z.lon }, map, content: dot, title: `${z.street} → HD ${dest}` });
          rlPinRef.current.push(m);
          bounds.extend({ lat: z.lat, lng: z.lon });
        }
        if (!bounds.isEmpty()) map.fitBounds(bounds, 60);
      } catch (e: any) { setMsg(e?.message || String(e)); }
    })();
    return () => { cancelled = true; };
  }, [rlDistPick, rlPreview, mode, ready]);

  // draggable pin for the selected household
  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map || !sel) return;
    const has = sel.lat != null && sel.lon != null;
    const pos = has ? { lat: sel.lat as number, lng: sel.lon as number } : (map.getCenter() as any);
    map.panTo(pos); if ((map.getZoom() || 0) < 19) map.setZoom(has ? 20 : 18);
    if (markerRef.current) markerRef.current.map = null;
    const m = new google.maps.marker.AdvancedMarkerElement({ position: pos, map, gmpDraggable: true, title: addr(sel), zIndex: 9999 });
    m.addListener("dragend", () => {
      const ll = m.position;
      const lat = typeof ll.lat === "function" ? ll.lat() : ll.lat;
      const lon = typeof ll.lng === "function" ? ll.lng() : ll.lng;
      setDragged({ lat, lon });
    });
    markerRef.current = m; setDragged(null); setSaved(false);
  }, [sel, ready]);

  // draggable pin for a BATCH placement (move whole stack / selected apartment units)
  useEffect(() => {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map || !placeTarget || sel) return;
    const pos = placeTarget.centroid ? { lat: placeTarget.centroid.lat, lng: placeTarget.centroid.lon } : (map.getCenter() as any);
    map.panTo(pos); if ((map.getZoom() || 0) < 19) map.setZoom(placeTarget.centroid ? 20 : 18);
    if (markerRef.current) markerRef.current.map = null;
    const m = new google.maps.marker.AdvancedMarkerElement({ position: pos, map, gmpDraggable: true, title: placeTarget.label, zIndex: 9999 });
    m.addListener("dragend", () => {
      const ll = m.position; const lat = typeof ll.lat === "function" ? ll.lat() : ll.lat; const lon = typeof ll.lng === "function" ? ll.lng() : ll.lng;
      setDragged({ lat, lon });
    });
    markerRef.current = m; setDragged(null);
  }, [placeTarget, sel, ready]);

  useEffect(() => { if (sel) setGeoQ(addr(sel)); }, [sel]);

  // Google Geocoder is a MAP-NAVIGATION aid only: it pans the map + moves the selected pin to a
  // starting spot. We never store Google's coordinate — the saved value is the human placement.
  function geocodeSearch(text: string) {
    const google = googleRef.current, map = mapRef.current;
    if (!google || !map || !text.trim()) return;
    setMsg(null);
    new google.maps.Geocoder().geocode(
      { address: text.trim(), componentRestrictions: { country: "US" } },
      (res: any, status: string) => {
        if (status === "REQUEST_DENIED") { setMsg("Google's Geocoding API is denied on this Maps key. In Google Cloud Console: enable the Geocoding API, and add it to the key's API restrictions (or set none)."); return; }
        if (status === "ZERO_RESULTS") { setMsg("Google couldn't find that address."); return; }
        if (status !== "OK" || !res || !res[0]) { setMsg("Google address lookup failed: " + status); return; }
        const loc = res[0].geometry.location;
        const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
        const lon = typeof loc.lng === "function" ? loc.lng() : loc.lng;
        map.panTo({ lat, lng: lon }); map.setZoom(20);
        if (sel && markerRef.current) { markerRef.current.position = { lat, lng: lon }; setDragged({ lat, lon }); }
        else { setMsg("Found it — pick an address on the left first, then search to move its pin onto the rooftop."); }
      });
  }

  async function reviewItem(h: HH, status: string) {
    try {
      await api(`/geocode/review`, { method: "POST", body: JSON.stringify({ household_id: h.id, status }) });
      setSuspects((rs) => rs.filter((s) => s.id !== h.id));
      const bm = layerRef.current.get(h.id); if (bm) { bm.map = null; layerRef.current.delete(h.id); }
      if (sel?.id === h.id) { setSel(null); setDragged(null); }
      pushLog({ kind: "review", label: `${status === "certified" ? "Looks fine" : "No such address"}: ${addr(h)}`, householdIds: [h.id], items: [h as Suspect] });
      setMsg(status === "certified" ? "Marked fine — removed from the queue." : "Marked no-such-address — removed from the queue.");
    } catch (e: any) { setMsg(e?.message || String(e)); }
  }

  async function undoLast() {
    if (!lastSaved) return;
    setBusy(true); setMsg(null);
    try {
      await api(`/geocode/undo`, { method: "POST", body: JSON.stringify({ household_id: lastSaved.id }) });
      if ((lastSaved as Suspect).reason) setSuspects((rs) => [lastSaved as Suspect, ...rs]);
      setMsg("Undone — restored the previous coordinate."); setLastSaved(null); setSaved(false); setSel(null);
    } catch (e: any) { setMsg(e?.message || String(e)); } finally { setBusy(false); }
  }

  async function save() {
    if (!sel || !dragged) return;
    const justSaved = sel;
    setBusy(true); setMsg(null);
    try {
      await api(`/geocode/correct`, { method: "POST", body: JSON.stringify({ household_id: sel.id, lat: dragged.lat, lon: dragged.lon }) });
      // quality must match what private.geocode_household_set actually writes (migration 0141) — an
      // optimistic copy that disagrees with the server is the drift rule 0 is about.
      const upd = { lat: dragged.lat, lon: dragged.lon, geocode_source: "human", geocode_quality: "rooftop_verified", is_placeholder_coord: false };
      setResults((rs) => rs.map((h) => (h.id === sel.id ? { ...h, ...upd } : h)));
      setHh((rs) => rs.map((h) => (h.id === sel.id ? { ...h, ...upd } : h)));
      // remove fixed suspect from the queue + its map dot
      setSuspects((rs) => rs.filter((h) => h.id !== sel.id));
      const bm = layerRef.current.get(sel.id);
      if (bm) { bm.map = null; layerRef.current.delete(sel.id); }
      if (curPrecinct) setCurPrecinct({ ...curPrecinct, n_verified: curPrecinct.n_verified + 1 });
      setSaved(true); setDragged(null); setLastSaved(justSaved);
      if ((justSaved as Suspect).reason) pushLog({ kind: "move", label: `Moved ${addr(justSaved)}`, householdIds: [justSaved.id], items: [justSaved as Suspect] });
      // cluster batch: auto-advance to the next member
      if (cluster) {
        const next = cluster.idx + 1;
        const remaining = cluster.ids.slice(next);
        const nextId = remaining.find((id) => id !== sel.id);
        if (nextId) {
          const nh = susRef.current.find((s) => s.id === nextId) || null;
          setCluster({ ...cluster, idx: next });
          setSel(nh);
        } else { setCluster(null); setSel(null); setMsg("Cluster done — all addresses placed."); }
      } else {
        setSel((s) => (s ? { ...s, ...upd } : s));
      }
    } catch (e: any) { setMsg(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  function startStackPlace(members: Suspect[], label: string) {
    setSel(null); setCluster(null); setSeparateKey(null); setMergePick(null);
    const withCoord = members.filter((m) => m.lat != null && m.lon != null);
    const centroid = withCoord.length
      ? { lat: withCoord.reduce((a, m) => a + (m.lat as number), 0) / withCoord.length,
          lon: withCoord.reduce((a, m) => a + (m.lon as number), 0) / withCoord.length }
      : null;
    setPlaceTarget({ ids: members.map((m) => m.id), label, centroid });
  }

  function startCluster(members: Suspect[]) {
    if (!members.length) return;
    setPlaceTarget(null); setSeparateKey(null); setDragged(null); setMergePick(null);
    const google = googleRef.current, map = mapRef.current;
    if (google && map) {
      const b = new google.maps.LatLngBounds();
      members.forEach((m) => m.lat != null && b.extend({ lat: m.lat, lng: m.lon as number }));
      map.fitBounds(b, 80);
    }
    setCluster({ ids: members.map((m) => m.id), idx: 0, key: members[0].cluster_key || "" });
    setSel(members[0]);
  }
  function skipCluster() {
    if (!cluster) return;
    const next = cluster.idx + 1;
    const nextId = cluster.ids[next];
    if (nextId) { setCluster({ ...cluster, idx: next }); setSel(susRef.current.find((s) => s.id === nextId) || null); }
    else { setCluster(null); setSel(null); setMsg("Reached the end of the cluster."); }
  }

  // triage groupings
  const clusterGroups = (() => {
    const m = new Map<string, Suspect[]>();
    for (const s of suspects) if (s.cluster_key) { const g = m.get(s.cluster_key); if (g) g.push(s); else m.set(s.cluster_key, [s]); }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  })();
  const singles = suspects.filter((s) => !s.cluster_key);
  const singlesByReason = (() => {
    const order = ["no_coord", "outside_precinct", "placeholder", "low_quality"];
    const m = new Map<string, Suspect[]>();
    for (const s of singles) { const g = m.get(s.reason); if (g) g.push(s); else m.set(s.reason, [s]); }
    return order.filter((r) => m.has(r)).map((r) => [r, m.get(r)!] as [string, Suspect[]]);
  })();

  const suspectCount = hh.filter((h) => statusOf(h) === "bad").length;
  const noCoord = hh.filter((h) => h.lat == null || h.lon == null);

  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 12, gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
        {/* NO <h2> HERE, for the reason Addresses.tsx gives: this was a top-level tab and kept its
            own title after moving into the Vault shell, so the page drew two. It also still said
            "Geocodes" -- the name deliberately retired when the sub-tab became "Voter-file
            cleanup", because "geocode" was doing duty for two unrelated jobs. The sentence below
            stays: it is an instruction about WHOSE file this is, not a title. */}
        <div className="row" style={{ gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {([["overview", "Auto-resolve"], ["realign", "Realign districts"], ["cleanup", "Triage leftovers"]] as const).map(([mv, label], i) => (
            <div key={mv} className="row" style={{ gap: 6, alignItems: "center" }}>
              {i > 0 && <span className="muted" style={{ fontSize: 12 }}>{"→"}</span>}
              <button className={"btn sm" + (mode === mv ? " accent" : "")} onClick={() => { setMode(mv); if (mv === "cleanup" && scopeValue && suspects.length === 0) scanCleanup(); }}>
                <span style={{ display: "inline-flex", width: 15, height: 15, borderRadius: 8, marginRight: 5, fontSize: 10, alignItems: "center", justifyContent: "center", background: mode === mv ? "rgba(255,255,255,.28)" : "rgba(128,128,128,.28)" }}>{i + 1}</span>{label}
              </button>
            </div>
          ))}
          <span style={{ width: 8 }} />
          <button className={"btn sm" + (mode === "search" ? " accent" : "")} onClick={() => setMode("search")}>Address search</button>
          <button className={"btn sm" + (mode === "browse" ? " accent" : "")} onClick={() => setMode("browse")}>Browse precinct</button>
          {/* VAULT COVERAGE AND WHERE TO SEND LIVE UNDER `Vault` NOW, and the move is by subject
              rather than by tidiness. This screen cleans the voter households of ONE district: it
              takes a district type and value, and every number on it is scoped to that district.
              Those two were statewide scorecards of our own address data sitting behind the same
              scope picker, which meant the picker was ignored on two of seven modes and the word
              "geocode" was doing duty for two unrelated jobs. */}
        </div>
        {/* WHOSE FILE THIS IS, SAID ON THE SCREEN. Everything here reads public.household -- the
            licensed VOTER file -- for one district. The address vault is a different table with a
            different set of problems and its own queues under Vault, and the two were being
            conflated by a sentence on Home that sent people from one to the other. */}
        <span className="muted" style={{ fontSize: 13 }}>
          Cleans the <b>voter file</b> for one district — not the address vault, which has its own
          queues under <b>Vault</b>. Drag a pin onto the rooftop and save; stored human-verified,
          and automated geocoding never overwrites it.
        </span>
      </div>
      {msg && <div className="err" style={{ marginBottom: 10 }}>{msg}</div>}
      {(mode === "overview" || mode === "browse" || mode === "cleanup" || mode === "realign") && (
        <div className="card" style={{ padding: 8, marginBottom: 10 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label className="fld" style={{ fontSize: 11, minWidth: 130 }}>District type
              <select className="ctl" style={{ fontSize: 12 }} value={scopeType} onChange={(e) => setScopeType(e.target.value)}>
                {SCOPE_TYPES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </label>
            <label className="fld" style={{ fontSize: 11, minWidth: 200 }}>{SCOPE_TYPES.find((s) => s.v === scopeType)?.label}
              <select className="ctl" style={{ fontSize: 12 }} value={scopeValue} onChange={(e) => setScopeValue(e.target.value)}>
                <option value="">— pick —</option>
                {regions.map((r) => <option key={r.code} value={r.code}>{r.name ? `${r.code} — ${r.name}` : r.code}</option>)}
              </select>
            </label>
            {mode === "overview"
              ? <button className="btn sm accent" disabled={!scopeValue || ovLoading} onClick={loadOverview}>{ovLoading ? "Scanning…" : "Load overview"}</button>
              : mode === "browse"
              ? <button className="btn sm accent" disabled={!scopeValue || loadingP} onClick={loadPrecincts}>{loadingP ? "Loading…" : "List precincts"}</button>
              : mode === "realign"
              ? <button className="btn sm accent" disabled={!scopeValue || rlBusy} onClick={() => realign(false)}>{rlBusy ? "Checking…" : "Preview realign"}</button>
              : <button className="btn sm accent" disabled={!scopeValue || loadingD} onClick={scanCleanup}>{loadingD ? "Scanning…" : "Scan for cleanup"}</button>}
            {mode === "cleanup" && (phantoms !== null || exactGroups.length > 0 || dupePairs.length > 0 || suspects.length > 0) && (
              <div className="row" style={{ gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
                {([[1, "Phantoms", phantoms?.count ?? 0],
                   [2, "Duplicates", (exactDupes?.groups ?? 0) + dupePairs.length],
                   [3, "Triage", suspects.length]] as [number, string, number][]).map(([nn, label, count]) => {
                  const cur = step === nn, done = step > nn;
                  return (
                    <button key={nn} className="btn sm" disabled={nn > step}
                      onClick={() => nn <= step && setStep(nn as 1 | 2 | 3)}
                      title={nn > step ? "Finish the earlier steps first" : ""}
                      style={{ minWidth: 96, opacity: nn > step ? 0.5 : 1,
                        background: cur ? "var(--accent,#3b82f6)" : done ? "rgba(63,192,121,.16)" : undefined,
                        color: cur ? "#fff" : undefined, fontWeight: cur ? 700 : 400 }}>
                      {done ? "✓ " : `${nn}. `}{label} · {count}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {/* FULL WIDTH, AND NO DISTRICT PICKER. Coverage is a statewide question about the vault; the
          scope bar above belongs to the per-district cleanup flow and would only be furniture here. */}

      {/* WHERE TO SEND WAS LEFT OUT OF THIS GUARD. `coverage` is excluded because it is a statewide
          question and the pin work surface below would be furniture; `confidence` is exactly the
          same kind of screen and was not, so "Where to send" rendered its county table and then a
          map, a Google box and "Pick a household to place its pin." underneath it, for a household
          it has no way to pick. Two full-width screens, one of them half in the pin editor. */}
      {(
      <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 12, alignItems: "start" }}>
        <div>
          {mode === "search" && (
            <>
              <form onSubmit={search} className="row" style={{ gap: 6, marginBottom: 10 }}>
                <input className="ctl" style={{ flex: 1 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Address, e.g. Red Fig Trl 78256" />
                <button className="btn accent" type="submit" disabled={busy}>{busy ? "…" : "Search"}</button>
              </form>
              <div className="card" style={{ padding: 0, maxHeight: 560, overflow: "auto" }}>
                {results.length === 0 ? <div className="muted" style={{ padding: 12 }}>Search an address to list matching households.</div>
                  : results.map((h) => <ResultRow key={h.id} h={h} sel={sel} onPick={setSel} />)}
              </div>
            </>
          )}

          {(mode === "overview" || mode === "browse" || mode === "cleanup" || mode === "realign") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {mode === "realign" && (
                <div className="card" style={{ padding: 12, border: "1px solid var(--accent, #3f7fe0)" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Realign districts from verified coordinates</div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                    Recomputes each house's State House / Congress / Senate district from its coordinate, against the
                    real state boundaries. Run after auto-resolve so coordinates are authority-verified. Precinct stays
                    county-authoritative (untouched). Click a destination below to see the moved houses inside that
                    district's boundary on the map.
                  </div>
                  <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <button className="btn sm accent" disabled={rlBusy || !scopeValue} onClick={() => realign(false)}>{rlBusy ? "Checking…" : "Preview realign"}</button>
                    {rlPreview && (rlPreview.changed_hd + rlPreview.changed_cd + rlPreview.changed_sd) > 0 && (
                      <button className="btn sm warn" disabled={rlBusy} onClick={async () => { if (await confirmDialog(`Reassign districts for ${rlPreview.changed_hd + rlPreview.changed_cd + rlPreview.changed_sd} house-fields from verified coordinates?\n\nHouse ${rlPreview.changed_hd} · Congress ${rlPreview.changed_cd} · Senate ${rlPreview.changed_sd}\nPrecinct is untouched. Reversible by re-running.`, { danger: true, confirmLabel: "Reassign" })) realign(true); }}>Apply</button>
                    )}
                  </div>
                  {rlPreview && (
                    <div style={{ marginTop: 8 }}>
                      {(rlPreview.changed_hd + rlPreview.changed_cd + rlPreview.changed_sd) === 0
                        ? <div className="muted" style={{ fontSize: 13 }}>Every house already matches its coordinate — nothing to realign.</div>
                        : (
                          <>
                            <div className="row" style={{ gap: 10, flexWrap: "wrap", fontSize: 12, marginBottom: 6 }}>
                              <span className="muted">will move:</span>
                              <span style={{ color: REASON.outside_precinct.color }}>{rlPreview.changed_hd} House</span>
                              <span style={{ color: REASON.placeholder.color }}>{rlPreview.changed_cd} Congress</span>
                              <span style={{ color: REASON.cluster.color }}>{rlPreview.changed_sd} Senate</span>
                            </div>
                            {Object.keys(rlPreview.hd_dests || {}).length > 0 && (
                              <div>
                                <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>House-district destinations — click one to confirm it on the map:</div>
                                <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                                  {Object.entries(rlPreview.hd_dests).sort((a, b) => (b[1] as number) - (a[1] as number)).map(([d, c]) => (
                                    <button key={d} className="btn sm" onClick={() => setRlDistPick(rlDistPick === d ? null : d)}
                                      style={{ background: rlDistPick === d ? "var(--accent,#3b82f6)" : undefined, color: rlDistPick === d ? "#fff" : undefined, fontWeight: rlDistPick === d ? 700 : 400 }}>
                                      HD {d} · {c as number}
                                    </button>
                                  ))}
                                </div>
                                {rlDistPick && <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>Green pins = inside HD {rlDistPick} (confirmed); red = landed outside (worth a look).</div>}
                              </div>
                            )}
                          </>
                        )}
                    </div>
                  )}
                  {rlResult && (
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      <b style={{ color: STATUS_COLOR.verified }}>Realigned {rlResult.changed_hd + rlResult.changed_cd + rlResult.changed_sd}</b> assignments across {rlResult.scanned} houses (House {rlResult.changed_hd} · Congress {rlResult.changed_cd} · Senate {rlResult.changed_sd}).
                      <div style={{ marginTop: 6 }}><button className="btn sm accent" onClick={() => { setMode("cleanup"); scanCleanup(); }}>Next: triage leftovers {"→"}</button></div>
                    </div>
                  )}
                </div>
              )}
              {mode === "overview" && !overview && (
                // WHAT STEP 1 IS, SAID IN THE COLUMN THAT IS OTHERWISE BLANK. Auto-resolve opens
                // with an empty left column, a "Save rooftop" button with nothing selected, a
                // Google address box and a satellite map of San Antonio -- a work surface for work
                // that cannot start until a district is picked. The one instruction was a grey
                // sentence in the map's toolbar, to the right of and below everything the eye
                // lands on first.
                <div className="card" style={{ padding: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Start with a district</div>
                  <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
                    Pick a district type and value above, then <b>Load overview</b>. Every step here works
                    one district at a time — statewide geocode cleanup is not a single job, and the
                    per-precinct numbers are what tell you where the work is.
                  </div>
                  <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 8 }}>
                    The overview lands in this column: doors, share verified, and per-precinct counts of
                    pins to fix, phantom pins and exact duplicates — each one a link into the work.
                    Then <b>2 Realign districts</b> recomputes districts from the verified coordinates, and
                    <b> 3 Triage leftovers</b> is the hand-work that is left.
                  </div>
                  <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 8 }}>
                    Looking for statewide numbers instead? <b>Vault → Coverage by county</b> grades all 254 counties.
                  </div>
                </div>
              )}
              {mode === "overview" && overview && (() => {
                const doors = overview.precincts.reduce((a, p) => a + p.n_total, 0);
                const verified = overview.precincts.reduce((a, p) => a + p.n_verified, 0);
                // AN EM DASH, NOT A ZERO. A district with no doors has not scored 0% verified; it has
                // not been scored. 0197, 0207 and 0210 each removed this from a different screen.
                const pct = doors ? `${Math.round((verified / doors) * 100)}%` : "—";
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div className="card" style={{ padding: 12 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>District health{(overview.phantoms == null || overview.dupeGroups == null || overview.suspects == null) && <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}> · some totals timed out (—) on this large district; the per-precinct list below is complete</span>}</div>
                      <div className="row" style={{ gap: 14, flexWrap: "wrap", fontSize: 13 }}>
                        <span><b>{doors.toLocaleString()}</b> doors</span>
                        <span style={{ color: STATUS_COLOR.verified }}><b>{pct}</b> verified</span>
                        <span style={{ color: REASON.outside_precinct.color }} title="No coordinate, outside its precinct, placeholder pin, stacked cluster, or low quality — the five reasons geocode_suspects flags.">
                          <b>{overview.suspects ?? "—"}</b> flagged, all reasons</span>
                        <span style={{ color: REASON.placeholder.color }}><b>{overview.phantoms == null ? "—" : overview.phantoms.toLocaleString()}</b> phantom pins</span>
                        <span style={{ color: REASON.cluster.color }}><b>{overview.dupeGroups ?? "—"}</b> exact dupes</span>
                      </div>
                      <div className="row" style={{ gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                        {/* DISABLED ONLY WHEN ALL THREE ARE MEASURED AND ZERO. They come back null when
                            the scan times out on a large district -- the note beside the heading says so --
                            and greying the button out then tells the reader there is no work when what we
                            have is no answer. Unmeasured is not zero, in a control as much as in a column. */}
                        <button className="btn sm accent"
                                disabled={overview.suspects === 0 && overview.dupeGroups === 0 && overview.phantoms === 0}
                                onClick={() => { setMode("cleanup"); scanCleanup(); }}>Clean up this district →</button>
                      </div>
                    </div>
                    <div className="card" style={{ padding: 12, border: "1px solid var(--accent, #3f7fe0)" }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Auto-resolve against authorities</div>
                      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                        Fixes only what <b>two</b> authorities agree on — confirms good pins, corrects wrong ZIPs,
                        re-pins bad coordinates. A pin moves only to a corroborated rooftop; disagreements are
                        left for you below.
                      </div>
                      {/* SAID BEFORE THE BUTTON, NOT AFTER THREE ZEROS. Corroboration needs two matched
                          authorities; outside Bexar there is one. The run is still correct there -- every
                          household lands in manual, which is the safe answer -- but a tool that reports
                          doing nothing reads as broken unless it says it is out of coverage. */}
                      {auth && !auth.known && (
                        <div className="muted" style={{ fontSize: 12, marginBottom: 8, paddingLeft: 8,
                                                        borderLeft: "2px solid var(--border,#27303f)" }}>
                          This district spans several counties, and a second authority covers only some of
                          them. Where there is only one, households fall to manual rather than being guessed.
                        </div>
                      )}
                      {auth && auth.known && !auth.can_corroborate && (
                        <div style={{ fontSize: 12, marginBottom: 8, paddingLeft: 8,
                                      borderLeft: `2px solid ${STATUS_COLOR.bad}`, color: "var(--muted,#8a98ad)" }}>
                          <b style={{ color: STATUS_COLOR.bad }}>Nothing can be auto-resolved in this county.</b>{" "}
                          Only <b>{auth.authorities.join(", ") || "no"}</b> covers it, and corroboration needs two —
                          so every household will land in the manual queue below. That is the tool being safe, not
                          broken. A county gains auto-resolve when its own locator is added as an authority.
                        </div>
                      )}
                      <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <button className="btn sm accent" disabled={arBusy || !scopeValue} onClick={() => autoResolve(false)}>{arBusy ? "Checking…" : "Preview auto-resolve"}</button>
                        {arPreview && (
                          <label className="row" style={{ gap: 4, fontSize: 12, alignItems: "center" }}>
                            <input type="checkbox" checked={arFixZip} onChange={(e) => setArFixZip(e.target.checked)} /> also correct wrong ZIPs
                          </label>
                        )}
                        {arPreview && (arPreview.certify + arPreview.zip_fix + arPreview.regeocode) > 0 && (
                          <button className="btn sm warn" disabled={arBusy} onClick={async () => { if (await confirmDialog(`Apply to ${arPreview.certify + arPreview.zip_fix + arPreview.regeocode} households?\n\n\u2713 confirm ${arPreview.certify} pins\n\u270e fix ${arFixZip ? arPreview.zip_fix : 0} ZIPs\n\u293a re-pin ${arPreview.regeocode}\n\nEvery change is audited and reversible; hand-placed pins are never touched.`, { danger: true, confirmLabel: "Apply" })) autoResolve(true); }}>Apply</button>
                        )}
                      </div>
                      {arPreview && (
                        <div style={{ marginTop: 8 }}>
                          <div className="row" style={{ gap: 10, flexWrap: "wrap", fontSize: 12 }}>
                            <span className="muted">of {arPreview.scanned} flagged:</span>
                            <span style={{ color: STATUS_COLOR.verified }}>&#10003; {arPreview.certify} confirm</span>
                            <span style={{ color: REASON.placeholder.color }}>&#9998; {arPreview.zip_fix} ZIP fix</span>
                            <span style={{ color: REASON.outside_precinct.color }}>&#10554; {arPreview.regeocode} re-pin</span>
                            <span style={{ color: STATUS_COLOR.bad }}>&#9995; {arPreview.manual} manual</span>
                          </div>
                          {arPreview.zip_mismatches.length > 0 && (
                            <details style={{ marginTop: 6 }}>
                              <summary className="muted" style={{ fontSize: 12, cursor: "pointer" }}>{arPreview.zip_mismatches.length} wrong ZIPs</summary>
                              <div style={{ maxHeight: 150, overflow: "auto", marginTop: 4 }}>
                                {arPreview.zip_mismatches.slice(0, 200).map((z) => (
                                  <div key={z.id} className="muted" style={{ fontSize: 11, padding: "2px 0" }}>{z.street} · {z.file_zip} → <b style={{ color: REASON.placeholder.color }}>{z.authority_zip}</b></div>
                                ))}
                              </div>
                            </details>
                          )}
                          {arPreview.regeocodes.length > 0 && (
                            <details style={{ marginTop: 4 }}>
                              <summary className="muted" style={{ fontSize: 12, cursor: "pointer" }}>{arPreview.regeocodes.length} pins to move</summary>
                              <div style={{ maxHeight: 150, overflow: "auto", marginTop: 4 }}>
                                {arPreview.regeocodes.slice(0, 200).map((z) => (
                                  <div key={z.id} className="muted" style={{ fontSize: 11, padding: "2px 0" }}>{z.street}{z.dist_m != null ? ` · moved ${arDist(z.dist_m)}` : ""}</div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      )}
                      {arResult && (
                        <div style={{ marginTop: 8, fontSize: 13 }}>
                          <div><b style={{ color: STATUS_COLOR.verified }}>Applied to {arResult.applied}</b> households{arResult.zip_fixed ? ` · ${arResult.zip_fixed} ZIPs corrected` : ""}.</div>
                          {arResult.manual > 0 && (
                            <div style={{ marginTop: 4 }}>
                              <span style={{ color: STATUS_COLOR.bad }}>{arResult.manual} still need a human.</span>{" "}
                              <button className="btn sm" onClick={() => { setMode("cleanup"); scanCleanup(); }}>Triage the rest →</button>
                            </div>
                          )}
                          <div style={{ marginTop: 6 }}><button className="btn sm accent" onClick={() => setMode("realign")}>Next: realign districts {"→"}</button></div>
                        </div>
                      )}
                    </div>
                    <div className="card" style={{ padding: 0, maxHeight: 460, overflow: "auto" }}>
                      <div style={{ padding: "8px 12px", borderBottom: BORDER, position: "sticky", top: 0, background: "var(--panel,#0f1621)" }}>
                        <div style={{ fontWeight: 700 }}>{overview.precincts.length} precincts</div>
                        {/* NAMED FOR WHAT IT COUNTS, because the line above counts something else.
                            "Flagged" up there is all five geocode_suspects reasons; this column is
                            placeholder + no-coordinate only, which is two of them. Both said "to fix",
                            twelve lines apart, and a precinct reading "clean" here can still hold pins
                            outside their precinct or stacked on one coordinate. */}
                        <div className="muted" style={{ fontSize: 11 }}>most missing pins first · click one to open it</div>
                      </div>
                      {[...overview.precincts].sort((a, b) => ((b.n_placeholder + b.n_nocoord) - (a.n_placeholder + a.n_nocoord))).map((p) => {
                        const tofix = p.n_placeholder + p.n_nocoord;
                        const vp = p.n_total ? Math.round((p.n_verified / p.n_total) * 100) : 0;
                        return (
                          <button key={(p.county || "") + p.precinct} onClick={() => openPrecinctFrom(p)}
                            style={{ display: "flex", width: "100%", justifyContent: "space-between", gap: 8, padding: "8px 12px", borderTop: BORDER, background: "transparent", cursor: "pointer", color: "inherit", textAlign: "left" }}>
                            <span><span style={{ display: "block", fontWeight: 700, fontSize: 14 }}>Precinct {p.precinct}</span>
                              <span className="muted" style={{ fontSize: 11 }}>{p.county || ""} · {p.n_total} doors · {vp}% verified</span></span>
                            <span style={{ textAlign: "right", fontSize: 11, whiteSpace: "nowrap" }}>
                              {tofix > 0 ? <span style={{ color: STATUS_COLOR.bad, display: "block", fontWeight: 700 }}>{tofix} no pin</span>
                                : <span className="muted" title="No placeholder or missing-coordinate pins here. Other flags — outside its precinct, stacked cluster, low quality — are not counted in this column.">no missing pins</span>}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {mode === "browse" && (curPrecinct ? (
                <div className="card" style={{ padding: 0, maxHeight: 520, overflow: "auto" }}>
                  <div style={{ padding: "10px 12px", borderBottom: BORDER, position: "sticky", top: 0, background: "var(--panel,#0f1621)" }}>
                    <button className="btn sm" onClick={() => { setCurPrecinct(null); setHh([]); setSel(null); }}>← precincts</button>
                    <div style={{ fontWeight: 700, marginTop: 6 }}>Precinct {curPrecinct.precinct}{curPrecinct.county ? ` · ${curPrecinct.county}` : ""}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {loadingH ? "Loading households…" : `${hh.length} doors · `}
                      <span style={{ color: STATUS_COLOR.bad }}>{suspectCount} to fix</span>{" · "}
                      <span style={{ color: STATUS_COLOR.verified }}>{hh.filter((h) => statusOf(h) === "verified").length} verified</span>
                    </div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                      <span style={{ color: STATUS_COLOR.bad }}>●</span> needs fixing ·
                      <span style={{ color: STATUS_COLOR.ok }}> ●</span> auto ·
                      <span style={{ color: STATUS_COLOR.verified }}> ●</span> verified — click a pin, drag to the rooftop, save
                    </div>
                  </div>
                  {noCoord.length > 0 && (
                    <div style={{ padding: "8px 12px", borderBottom: BORDER }}>
                      <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>{noCoord.length} with no coordinate — click to place at map center:</div>
                      {noCoord.slice(0, 40).map((h) => (
                        <button key={h.id} onClick={() => setSel(h)} style={{ display: "block", width: "100%", textAlign: "left", padding: "3px 0", background: "transparent", cursor: "pointer", color: "inherit", fontSize: 12 }}>{addr(h)}</button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="card" style={{ padding: 0, maxHeight: 520, overflow: "auto" }}>
                  {precincts.length === 0 ? <div className="muted" style={{ padding: 12 }}>Pick a district and click “List precincts”.</div>
                    : precincts.map((p) => (
                      <button key={(p.county || "") + p.precinct} onClick={() => openPrecinct(p)}
                        style={{ display: "flex", width: "100%", justifyContent: "space-between", gap: 8, padding: "8px 12px", borderTop: BORDER, background: "transparent", cursor: "pointer", color: "inherit", textAlign: "left" }}>
                        <span>
                          <span style={{ display: "block", fontWeight: 700, fontSize: 14 }}>Precinct {p.precinct}</span>
                          <span className="muted" style={{ fontSize: 11 }}>{p.county || ""} · {p.n_total} doors</span>
                        </span>
                        <span style={{ textAlign: "right", fontSize: 11, whiteSpace: "nowrap" }}>
                          {(p.n_placeholder + p.n_nocoord) > 0 && <span style={{ color: STATUS_COLOR.bad, display: "block" }}>{p.n_placeholder + p.n_nocoord} to fix</span>}
                          {p.n_verified > 0 && <span style={{ color: STATUS_COLOR.verified, display: "block" }}>{p.n_verified} verified</span>}
                        </span>
                      </button>
                    ))}
                </div>
              ))}

              {/* `|| anyScanFailed` so the whole step UI does not vanish when every scan fails —
                  otherwise all that remains is a banner, which reads like the tool is broken rather
                  than like the district was never scanned. */}
              {mode === "cleanup" && (phantoms !== null || exactDupes || dupePairs.length > 0 || suspects.length > 0
                                      || scanFailed.phantoms || scanFailed.dupes || scanFailed.suspects) && (
                <>
                  {step === 1 && (
                    <div className="card" style={{ padding: 12 }}>
                      <div style={{ fontWeight: 700 }}>Step 1 · Remove phantom pins</div>
                      <div className="muted" style={{ fontSize: 12, margin: "4px 0 8px" }}>
                        Bare street names (no house number) with zero registered voters — county parcel-service
                        placeholders left from subdivision build-out. Safe to sweep, reversible.
                        {(phantoms?.count ?? 0) > 0 && ((phantoms?.blank_stubs ?? 0) + (phantoms?.street_only ?? 0) > 0) && (
                          <> <b>{phantoms?.blank_stubs}</b> blank stubs + <b>{phantoms?.street_only}</b> street-only placeholders.</>
                        )}
                      </div>
                      {phantoms && phantoms.count > 0 ? (
                        <>
                          <button className="btn warn" disabled={busy} onClick={removePhantoms}>
                            Remove {phantoms.count} no voter/no address pins
                          </button>
                          <div className="card" style={{ padding: 0, marginTop: 8, maxHeight: 150, overflow: "auto", background: "transparent" }}>
                            {phantoms.sample.slice(0, 100).map((h) => (
                              <div key={h.id} className="muted" style={{ fontSize: 11, padding: "2px 8px", borderTop: BORDER, userSelect: "text" }}>{addr(h)}</div>
                            ))}
                            {phantoms.count > phantoms.sample.length && <div className="muted" style={{ fontSize: 11, padding: "3px 8px" }}>… and {phantoms.count - phantoms.sample.length} more</div>}
                          </div>
                        </>
                      ) : scanFailed.phantoms ? <div className="muted" style={{ fontSize: 12, color: "var(--warn,#eab308)" }}>Couldn't scan this step — re-scan to retry. This is not a clean result.</div>
                        : <div className="muted" style={{ fontSize: 12 }}>None in this scope — nothing to sweep.</div>}
                      <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}>
                        <button className="btn accent" onClick={() => setStep(2)}>Next: Duplicates →</button>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div className="card" style={{ padding: 12 }}>
                        <div style={{ fontWeight: 700 }}>Step 2 · Merge duplicates</div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                          The same address written more than once, voters split across the copies. Merging only unions
                          distinct voters onto one record — counts never inflate.
                        </div>
                      </div>
                      {exactGroups.length > 0 && (
                        <div className="card" style={{ padding: 0, maxHeight: 360, overflow: "auto" }}>
                          <div className="muted" style={{ padding: "6px 12px", borderBottom: BORDER, fontSize: 11 }}>Review each exact-duplicate group before merging · click a row to expand · Map plots its members</div>
                          {exactGroups.map((g) => {
                            const surv = g.members.find((m) => m.survivor) || g.members[0];
                            const isOpen = openGroups.has(g.key);
                            return (
                              <div key={g.key} style={{ borderTop: BORDER }}>
                                <div className="row" style={{ justifyContent: "space-between", gap: 6, padding: "8px 12px", cursor: "pointer", flexWrap: "wrap" }}
                                  onClick={() => setOpenGroups((s) => { const n = new Set(s); if (n.has(g.key)) n.delete(g.key); else n.add(g.key); return n; })}>
                                  <span style={{ minWidth: 0, fontSize: 12 }}>
                                    <span style={{ fontWeight: 700 }}>{isOpen ? "▾ " : "▸ "}{memAddr(surv)}</span>
                                    <span className="muted"> · {g.count} records</span>
                                    {!g.coords_uniform && <span className="badge" style={{ marginLeft: 6, fontSize: 10, color: "#fff", background: "#e0553f" }}>⚠ coordinates differ — review</span>}
                                  </span>
                                  <span className="row" style={{ gap: 6 }} onClick={(e) => e.stopPropagation()}>
                                    <button className="btn sm" title="Show these members on the map" onClick={() => previewHouseholds(g.members)}>Map</button>
                                    <button className="btn sm accent" disabled={busy} title="Merge just this group into its survivor" onClick={() => mergeGroup(g)}>Merge this</button>
                                  </span>
                                </div>
                                {isOpen && (
                                  <div style={{ padding: "0 12px 8px 24px" }}>
                                    {g.members.map((m) => (
                                      <div key={m.id} className="row" style={{ gap: 6, alignItems: "baseline", padding: "2px 0", fontSize: 12 }}>
                                        <span style={{ minWidth: 0, userSelect: "text" }}>{memAddr(m)}</span>
                                        <span className="muted" style={{ fontSize: 11 }}> · {m.lat != null && m.lon != null ? `${m.lat.toFixed(5)}, ${m.lon.toFixed(5)}` : "no coord"} · {m.voters} voter{m.voters === 1 ? "" : "s"}</span>
                                        {m.survivor && <span className="badge" style={{ fontSize: 10, color: "#3fc079" }}>keep</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {exactDupes && exactDupes.groups > 0 && (
                        <div className="card" style={{ padding: 12 }}>
                          <div style={{ fontWeight: 700 }}>Exact duplicates</div>
                          <div className="muted" style={{ fontSize: 12, margin: "4px 0 8px" }}>
                            {exactDupes.groups} address{exactDupes.groups === 1 ? "" : "es"} written more than once (identical street + ZIP + unit).
                          </div>
                          <button className="btn accent" disabled={busy} onClick={mergeExactAll}>
                            Merge all {exactDupes.groups} exact duplicates ({exactDupes.extra_records} extra record{exactDupes.extra_records === 1 ? "" : "s"})
                          </button>
                        </div>
                      )}
                      {dupePairs.length > 0 && (
                        <div className="card" style={{ padding: 0, maxHeight: 420, overflow: "auto" }}>
                          <div style={{ padding: "8px 12px", borderBottom: BORDER, position: "sticky", top: 0, background: "var(--panel,#0f1621)" }}>
                            <div style={{ fontWeight: 700 }}>Needs a look <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· same spot, different name</span></div>
                            <div className="muted" style={{ fontSize: 11 }}>
                              {`${dupePairs.length}${dupeTotal > dupePairs.length ? ` of ${dupeTotal}` : ""} colocated pairs — decide each one`}
                            </div>
                          </div>
                          {["near", "review"].filter((t) => dupePairs.some((pr) => pr.tier === t)).map((t) => (
                            <div key={t}>
                              <div style={{ padding: "6px 12px", borderTop: BORDER, fontSize: 11, fontWeight: 700, color: t === "near" ? "#3fc079" : "#e08b3f" }}>
                                {t === "near" ? "Likely duplicate — near-identical name at the same spot" : "Same spot, different street name — review (often a bad geocode)"}
                              </div>
                              {dupePairs.filter((pr) => pr.tier === t).map((pr) => {
                                const surv = chosenSurvivor(pr);
                                return (
                                  <div key={pairKey(pr)} style={{ padding: "8px 12px", borderTop: BORDER }}>
                                    <div className="row" style={{ justifyContent: "space-between", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                                      <span style={{ fontSize: 11 }}>
                                        <span className="badge" style={{ fontSize: 10 }}>same spot</span>
                                        <span className="muted"> · name match {Math.round(pr.sim * 100)}%</span>
                                      </span>
                                      <span className="row" style={{ gap: 6 }}>
                                        <button className="btn sm" onClick={() => focusPair(pr)} title="Show both pins on the map">Map</button>
                                        <button className="btn sm accent" disabled={busy} onClick={() => mergePair(pr)} title="Merge the two into the survivor">Merge</button>
                                      </span>
                                    </div>
                                    {([pr.a, pr.b]).map((h) => (
                                      <label key={h.id} className="row" style={{ gap: 6, alignItems: "baseline", padding: "2px 0", cursor: "pointer" }}>
                                        <input type="radio" name={"surv_" + pairKey(pr)} checked={surv === h.id}
                                          onChange={() => setSurvivorPick((m) => ({ ...m, [pairKey(pr)]: h.id }))} />
                                        <span style={{ minWidth: 0, fontSize: 12 }}>
                                          <span style={{ fontWeight: surv === h.id ? 700 : 400, userSelect: "text" }}>{sideAddr(h)}</span>
                                          <span className="muted" style={{ fontSize: 11 }}> · {h.voters} voter{h.voters === 1 ? "" : "s"}{h.source === "human" ? " · human-verified" : ""}{surv === h.id ? " · keep" : ""}</span>
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                      {(!exactDupes || exactDupes.groups === 0) && dupePairs.length === 0 && (
                        <div className="card" style={{ padding: 12 }}>
                          {scanFailed.dupes ? <div className="muted" style={{ fontSize: 12, color: "var(--warn,#eab308)" }}>Couldn't scan this step — re-scan to retry. This is not a clean result.</div>
                            : <div className="muted" style={{ fontSize: 12 }}>No duplicates in this scope — nothing to merge.</div>}
                        </div>
                      )}
                      <div className="row" style={{ justifyContent: "space-between" }}>
                        <button className="btn sm" onClick={() => setStep(1)}>← Back</button>
                        <button className="btn accent" onClick={() => setStep(3)}>Next: Triage →</button>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div className="card" style={{ padding: 0, maxHeight: 520, overflow: "auto" }}>
                        <div style={{ padding: "8px 12px", borderBottom: BORDER, position: "sticky", top: 0, background: "var(--panel,#0f1621)" }}>
                          <div className="row" style={{ gap: 6 }}>
                            <button className="btn sm" onClick={() => setHistOpen(false)} style={{ minWidth: 90, background: !histOpen ? "var(--accent,#3b82f6)" : undefined, color: !histOpen ? "#fff" : undefined, fontWeight: !histOpen ? 700 : 400 }}>To fix ({suspects.length})</button>
                            <button className="btn sm" onClick={() => { setHistOpen(true); loadHistory(); }} style={{ minWidth: 90, background: histOpen ? "var(--accent,#3b82f6)" : undefined, color: histOpen ? "#fff" : undefined, fontWeight: histOpen ? 700 : 400 }}>History</button>
                          </div>
                          {!histOpen && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>stacks first · move a whole apartment stack, separate a mixed one, or reposition singles</div>}
                        </div>
                        {!histOpen ? (suspects.length === 0 ? (
                          <div style={{ padding: 12 }}>
                            {/* THE SENTENCE THAT MATTERS. This is what a staffer reads before signing
                                a district off to cut walkbooks from, so it must never appear because
                                a scan timed out. */}
                            {scanFailed.suspects ? <div className="muted" style={{ fontSize: 12, color: "var(--warn,#eab308)" }}>Couldn't scan this step — re-scan to retry. This is not a clean result.</div>
                              : <div className="muted" style={{ fontSize: 12 }}>Nothing left to fix — this district's geocodes look clean.</div>}
                          </div>
                        ) : (<>
                          {clusterGroups.map(([key, members]) => {
                            const apts = members.filter(isApt);
                            const sings = members.filter((m) => !isApt(m));
                            const open = separateKey === key;
                            return (
                              <div key={key} style={{ padding: "8px 12px", borderTop: BORDER }}>
                                <div className="row" style={{ justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                                  <span><span style={{ color: REASON.cluster.color, fontWeight: 700 }}>⚠ Stacked cluster</span>
                                    <span className="muted" style={{ fontSize: 12 }}> · {members.length} on one spot{apts.length && sings.length ? ` (${apts.length} apt · ${sings.length} standalone)` : apts.length && !sings.length ? " (all apartments)" : ""}</span></span>
                                  <span style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                                    <span className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                                      <button className="btn sm" title="Walk this stack one address at a time on the map" onClick={() => startCluster(members)}>Map</button>
                                      <button className="btn sm" title="Drop one pin and move every address in this stack onto it" onClick={() => startStackPlace(members, `whole stack · ${members.length} addresses`)}>Move whole stack</button>
                                      <button className={"btn sm" + (open ? " accent" : "")} title="Split apartments from standalones and place them separately" onClick={() => { setCluster(null); setSel(null); setPlaceTarget(null); setDragged(null); setMergePick(null); setSeparateKey(open ? null : key); }}>Separate{open ? " ▾" : ""}</button>
                                    </span>
                                    <span className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                                      <button className={"btn sm" + (mergePick?.key === key ? " accent" : "")} title="Merge every record in this stack into one household" onClick={() => { setCluster(null); setSel(null); setPlaceTarget(null); setSeparateKey(null); setDragged(null); setMergePick(mergePick?.key === key ? null : { key, members, survivorId: recommendSurvivor(members) }); }}>Merge</button>
                                      <button className="btn sm" title="These geocodes are fine as-is" onClick={() => members.forEach((m) => reviewItem(m, "certified"))}>Looks fine</button>
                                    </span>
                                  </span>
                                </div>
                                {!open && members.slice(0, 3).map((sp) => <div key={sp.id} className="muted" style={{ fontSize: 11 }}>{addr(sp)}</div>)}
                                {!open && members.length > 3 && <div className="muted" style={{ fontSize: 11 }}>+{members.length - 3} more</div>}
                                {mergePick && mergePick.key === key && (
                                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6, borderTop: BORDER, paddingTop: 8 }}>
                                    <div className="muted" style={{ fontSize: 11 }}>Keep which record? The others merge into it (voters union — safe). Recommended is preselected.</div>
                                    {members.map((m) => (
                                      <label key={m.id} className="row" style={{ gap: 6, alignItems: "baseline", cursor: "pointer" }}>
                                        <input type="radio" name={"surv_" + key} checked={mergePick.survivorId === m.id} onChange={() => setMergePick({ ...mergePick, survivorId: m.id })} />
                                        <span style={{ minWidth: 0, fontSize: 12 }}>
                                          <span style={{ fontWeight: mergePick.survivorId === m.id ? 700 : 400, userSelect: "text" }}>{addr(m)}</span>
                                          <span className="muted" style={{ fontSize: 11 }}>{m.lat != null ? ` · ${(m.lat as number).toFixed(5)}, ${(m.lon as number).toFixed(5)}` : " · no coord"}{m.geocode_source === "human" ? " · verified" : ""}</span>
                                        </span>
                                      </label>
                                    ))}
                                    <div className="row" style={{ gap: 6, marginTop: 2 }}>
                                      <button className="btn sm accent" disabled={busy} onClick={doMergeCluster}>Merge into this</button>
                                      <button className="btn sm" disabled={busy} onClick={() => setMergePick(null)}>Cancel</button>
                                    </div>
                                  </div>
                                )}
                                {open && (
                                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8, borderTop: BORDER, paddingTop: 8 }}>
                                    <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                                      <span className="muted" style={{ fontSize: 11 }}>Move the apartments together; place each standalone on its own rooftop.</span>
                                      <button className="btn sm" title="Close the separate view" onClick={() => setSeparateKey(null)}>✕ Close</button>
                                    </div>
                                    {apts.length > 0 && (
                                      <div style={{ borderLeft: `2px solid ${REASON.cluster.color}`, paddingLeft: 8 }}>
                                        <div className="row" style={{ justifyContent: "space-between", gap: 6, flexWrap: "wrap" }}>
                                          <span style={{ fontSize: 12, fontWeight: 700 }}>Apartments · {apts.length} unit{apts.length === 1 ? "" : "s"}</span>
                                          <button className="btn sm" title="Move all these units onto one building rooftop" onClick={() => startStackPlace(apts, `${apts.length} apartment units`)}>Move all here</button>
                                        </div>
                                        {apts.slice(0, 40).map((sp) => <div key={sp.id} className="muted" style={{ fontSize: 11, userSelect: "text" }}>{addr(sp)}</div>)}
                                        {apts.length > 40 && <div className="muted" style={{ fontSize: 11 }}>+{apts.length - 40} more</div>}
                                      </div>
                                    )}
                                    {sings.length > 0 && (
                                      <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>Standalone · place individually</div>
                                        {sings.map((sp) => (
                                          <div key={sp.id} className="row" style={{ gap: 6, alignItems: "center", padding: "2px 0" }}>
                                            <span style={{ flex: 1, minWidth: 0, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", userSelect: "text" }}>{addr(sp)}</span>
                                            <button className="btn sm" title="Reposition this address on the map" onClick={() => { setPlaceTarget(null); setCluster(null); setSel(sp); }}>Place</button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {singlesByReason.map(([reason, rows]) => (
                            <div key={reason}>
                              <div style={{ padding: "6px 12px", borderTop: BORDER, fontSize: 11, fontWeight: 700, color: reasonColor(reason) }}>
                                {REASON[reason]?.label || reason} ({rows.length})
                              </div>
                              {rows.slice(0, 200).map((sp) => (
                                <div key={sp.id} style={{ padding: "6px 12px", borderTop: BORDER, background: sel?.id === sp.id ? "rgba(80,130,255,.14)" : "transparent" }}>
                                  <div className="row" style={{ gap: 6, alignItems: "center" }}>
                                    <span style={{ color: reasonColor(sp.reason) }}>●</span>
                                    <span onClick={() => { setPlaceTarget(null); setCluster(null); setSel(sp); }} title="Click to reposition on the map"
                                      style={{ flex: 1, minWidth: 0, fontSize: 13, cursor: "pointer", userSelect: "text", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{addr(sp)}</span>
                                  </div>
                                  <div className="row" style={{ gap: 6, marginTop: 4, paddingLeft: 16 }}>
                                    <button className="btn sm" title="This geocode is fine as-is" onClick={() => reviewItem(sp, "certified")}>Looks fine</button>
                                    <button className="btn sm warn" title="This address doesn't exist" onClick={() => reviewItem(sp, "no_such_address")}>No such address</button>
                                  </div>
                                </div>
                              ))}
                              {rows.length > 200 && <div className="muted" style={{ fontSize: 11, padding: "4px 12px" }}>+{rows.length - 200} more — fix these, then reload</div>}
                            </div>
                          ))}
                        </>)) : (
                          <div>
                            <div className="muted" style={{ padding: "6px 12px", borderBottom: BORDER, fontSize: 11 }}>Fixes made in this district — undo any of them here.</div>
                            {histLoading ? (
                              <div className="muted" style={{ padding: 12, fontSize: 12 }}>Loading history…</div>
                            ) : history.length === 0 ? (
                              <div className="muted" style={{ padding: 12, fontSize: 12 }}>No recorded fixes in this district yet.</div>
                            ) : history.map((ev, i) => {
                              const label = ev.kind === "moved" ? "Moved" : ev.kind === "merged" ? "Merged" : ev.kind === "certified" ? "Looks fine" : ev.kind === "no_such_address" ? "No such address" : ev.kind;
                              return (
                                <div key={ev.ref + "|" + i} className="row" style={{ justifyContent: "space-between", gap: 8, padding: "6px 12px", borderTop: BORDER, alignItems: "center", opacity: ev.undone ? 0.5 : 1 }}>
                                  <span style={{ minWidth: 0, fontSize: 12 }}>
                                    <span style={{ fontWeight: 700 }}>{label}</span>
                                    <span className="muted"> · </span>
                                    <span style={{ userSelect: "text" }}>{ev.address}</span>
                                    {ev.at && <div className="muted" style={{ fontSize: 11 }}>{new Date(ev.at).toLocaleString()}</div>}
                                  </span>
                                  {ev.undone
                                    ? <span className="muted" style={{ fontSize: 11 }}>undone</span>
                                    : <button className="btn sm" disabled={busy} onClick={() => undoHistory(ev)} title="Undo this fix">Undo</button>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="row" style={{ justifyContent: "space-between", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              {cluster
                ? <b style={{ color: REASON.cluster.color }}>Fixing cluster · {Math.min(cluster.idx + 1, cluster.ids.length)} of {cluster.ids.length}{sel ? ` — ${addr(sel)}` : ""}</b>
                : placeTarget ? <b style={{ color: REASON.cluster.color }}>Placing {placeTarget.label} — drag the pin onto the rooftop, then Place</b>
                : sel ? <b>{addr(sel)}</b>
                : mode === "overview" ? <span className="muted">{overview ? "Click a precinct on the left to jump into its work." : "Waiting on a district — see the panel on the left."}</span>
                : mode === "cleanup" ? <span className="muted">Scan a district, then work the steps on the left — sweep phantoms, merge duplicates, reposition what's left. Pins and placement show here.</span>
                : <span className="muted">{mode === "browse" ? "Open a precinct, then click a pin to reposition it." : "Pick a household to place its pin."}</span>}
              {sel && !cluster && (sel as Suspect).reason && <span className="muted" style={{ fontSize: 12 }}> · {REASON[(sel as Suspect).reason]?.label || ""}</span>}
              {dragged && <div className="muted" style={{ fontSize: 11 }}>Moved to {dragged.lat.toFixed(6)}, {dragged.lon.toFixed(6)} — not saved.</div>}
              {saved && !dragged && !cluster && <div style={{ fontSize: 11, color: "#3fc079", fontWeight: 700 }}>Saved — human-verified rooftop.</div>}
            </div>
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              {mode === "cleanup" && step === 3 && sel && !cluster && !placeTarget && !dragged && <>
                <button className="btn sm" title="This geocode is actually fine" onClick={() => reviewItem(sel, "certified")} disabled={busy}>Looks fine</button>
                <button className="btn sm warn" title="This address does not exist" onClick={() => reviewItem(sel, "no_such_address")} disabled={busy}>No such address</button>
              </>}
              {mode === "cleanup" && step === 2 && lastMerge && <button className="btn sm" onClick={undoMerge} disabled={busy} title="Undo the last merge — repoint records back">Undo merge</button>}
              {mode === "cleanup" && step === 2 && lastBulk && lastBulk.length > 0 && <button className="btn sm" onClick={undoBulk} disabled={busy} title="Undo the bulk merge — repoint all records back">Undo {lastBulk.length} merges</button>}
              {lastSaved && !dragged && !placeTarget && <button className="btn sm" onClick={undoLast} disabled={busy} title="Restore the previous coordinate">Undo</button>}
              {cluster && <button className="btn sm" onClick={skipCluster} disabled={busy}>Skip</button>}
              {cluster && <button className="btn sm" onClick={() => { setCluster(null); setSel(null); }} disabled={busy}>Exit cluster</button>}
              {placeTarget && <button className="btn sm" onClick={() => { setPlaceTarget(null); setDragged(null); }} disabled={busy}>Cancel</button>}
              {placeTarget
                ? <button className="btn accent" onClick={savePlacement} disabled={!dragged || busy}>Place {placeTarget.ids.length} here</button>
                : (sel || cluster) && <button className="btn accent" onClick={save} disabled={!dragged || busy}>Save rooftop</button>}
            </div>
          </div>
          {!(mode === "overview" && !overview) && (
            <form onSubmit={(e) => { e.preventDefault(); geocodeSearch(geoQ); }} className="row" style={{ gap: 6, marginBottom: 6 }}>
              <input className="ctl" style={{ flex: 1 }} value={geoQ} onChange={(e) => setGeoQ(e.target.value)} placeholder="Find an address on Google to jump the map + pin here" />
              <button className="btn sm" type="submit" title="Uses Google only to move the map + pin here; you still place it on the rooftop and save">Find on Google</button>
            </form>
          )}
          {mode === "overview" && !overview && (
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              The map is idle until a district is loaded — it will centre on the work.
            </div>
          )}
          <div ref={elRef} style={{ width: "100%", height: "clamp(430px,60vh,760px)", borderRadius: 10, overflow: "hidden", border: BORDER,
                                    opacity: mode === "overview" && !overview ? 0.45 : 1, transition: "opacity .2s" }} />
        </div>
      </div>
      )}
    </div>
  );
}

function ResultRow({ h, sel, onPick }: { h: HH; sel: HH | null; onPick: (h: HH) => void }) {
  return (
    <button onClick={() => onPick(h)}
      style={{ display: "flex", width: "100%", textAlign: "left", justifyContent: "space-between", gap: 8, padding: "8px 12px", borderTop: BORDER, background: sel?.id === h.id ? "rgba(80,130,255,.14)" : "transparent", cursor: "pointer", color: "inherit" }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{addr(h)}</span>
        <span className="muted" style={{ display: "block", fontSize: 11 }}>{h.precinct ? `Precinct ${h.precinct} · ` : ""}{h.lat != null ? `${(h.lat as number).toFixed(5)}, ${(h.lon as number).toFixed(5)}` : "no coordinate"}</span>
      </span>
      <span className={"badge" + (h.geocode_source === "human" ? " active" : "")} style={{ fontSize: 10, whiteSpace: "nowrap" }}>{h.geocode_source === "human" ? "human-verified" : (h.geocode_source || h.geocode_quality || "—")}</span>
    </button>
  );
}
